"use client";

import { useState } from "react";
import {
  Checkbox,
  Notice,
  SelectField,
} from "@/components/tool-kit/primitives";
import { LABEL, WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  ArticlePanel,
  CodeFields,
  dataNotices,
  NonePanel,
  PublicPanel,
  RepositoryPanel,
  type RepositoryRow,
  RequestPanel,
  RestrictedPanel,
} from "@/components/tools/publishing-statements/data-panels";
import {
  ExampleButton,
  NoticeList,
  StatementCard,
} from "@/components/tools/publishing-statements/kit";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  type CodeInputs,
  type CodeMode,
  codeAvailabilityStatement,
  type DataInputs,
  type DataScenarioId,
  dataAvailabilityStatement,
  toggleScenario,
} from "@/lib/tool-kit/publishing/data-availability";
import { plainParagraph } from "@/lib/tool-kit/publishing/rich-text";

/** English strings. A translated content file overrides these through `ui`. */
export const DATA_AVAILABILITY_STATEMENT_GENERATOR_STRINGS = {
  scenariosLegend:
    "Where are the data behind your findings? Tick every case that applies.",
  scenarioArticle: "In the article or its supplementary materials",
  scenarioRepository: "In a public repository (DOI or accession number)",
  scenarioPublic: "Reused from existing public sources",
  scenarioRestricted:
    "Restricted — privacy, ethics, or a third party's license",
  scenarioRequest: "Available from the authors on reasonable request",
  scenarioNone: "No data were generated or analyzed",
  articlePanel: "In the article",
  supplementaryLabel: "Some of the data are in supplementary files",
  filesLabel: "Supplementary files (optional)",
  filesPlaceholder: "e.g. Tables S1–S3 and Data S1",
  repositoryPanel: "Repository deposit {number}",
  repositoryLabel: "Repository",
  repositoryPlaceholder: "e.g. Zenodo, Dryad, Gene Expression Omnibus",
  identifierLabel: "DOI, accession number or URL",
  identifierPlaceholder: "e.g. 10.5061/dryad.example",
  identifierHint:
    "A DOI is printed as an https://doi.org/ link; anything else exactly as you type it.",
  descriptionLabel: "What this deposit holds (optional)",
  descriptionPlaceholder: "e.g. anonymized survey responses",
  licenseLabel: "License (optional)",
  licensePlaceholder: "e.g. CC BY 4.0",
  embargoLabel: "Embargoed until (optional)",
  addRepository: "Add another repository",
  removeRepository: "Remove",
  removeRepositoryAria: "Remove repository deposit {number}",
  publicPanel: "Existing public data",
  sourcesLabel: "Sources and where to find them",
  sourcesPlaceholder:
    "e.g. the World Bank's World Development Indicators (https://databank.worldbank.org)",
  restrictedPanel: "Restricted data",
  restrictionLabel: "Why access is restricted",
  restrictionPrivacy: "Participant privacy",
  restrictionEthics: "Terms of the ethics approval",
  restrictionThirdParty: "Owned by a third party (used under license)",
  restrictionLegal: "Legal or commercial restrictions",
  restrictedDescriptionLabel: "Which data (optional — blank means all)",
  restrictedDescriptionPlaceholder: "e.g. interview transcripts",
  partyLabel: "Who owns the data",
  partyPlaceholder: "e.g. the national cancer registry",
  accessContactLabel: "Where to send access requests",
  accessContactPlaceholder:
    "e.g. the Data Access Committee of the Eastbrook Institute",
  accessDetailsLabel: "Contact details (optional)",
  accessDetailsPlaceholder: "e.g. an email address or web page",
  conditionsLabel: "Conditions of access (optional)",
  conditionsPlaceholder: "e.g. a signed data use agreement",
  requestPanel: "Available on request",
  requestContactLabel: "Who to contact (optional)",
  requestReasonLabel: "Why the data are not public (optional)",
  requestReasonPlaceholder:
    "e.g. they contain commercially sensitive information",
  nonePanel: "No data",
  noneReasonLabel: "Reason",
  noneNoDataOption: "No datasets were generated or analyzed",
  noneTheoreticalOption: "The work is purely theoretical",
  codeLabel: "Code availability statement",
  codeOff: "Don't add one",
  codeRepositoryOption: "Code in a public repository",
  codeRequestOption: "Code available on request",
  codeNoneOption: "No custom code was written",
  codeRepositoryPlaceholder: "e.g. Zenodo, Software Heritage",
  codeIdentifierPlaceholder: "e.g. 10.5281/zenodo.example",
  codeDescriptionLabel: "What the code is (optional)",
  codeDescriptionPlaceholder: "e.g. R scripts for the analyses",
  codeLicensePlaceholder: "e.g. MIT",
  softwareLabel: "Software used (optional)",
  softwarePlaceholder: "e.g. SPSS 29 and R 4.4",
  tryExample: "Try an example",
  exampleRepository: "the Open Science Framework",
  exampleIdentifier: "10.17605/OSF.IO/EXAMPLE",
  exampleDescription: "anonymized survey responses",
  exampleLicense: "CC BY 4.0",
  exampleRestricted: "interview transcripts",
  exampleAccessContact:
    "the Research Data Office of the University of Eastbrook",
  exampleAccessDetails: "data.access@eastbrook.example",
  exampleConditions: "a signed data use agreement",
  exampleCodeRepository: "Zenodo",
  exampleCodeIdentifier: "10.5281/zenodo.example",
  exampleCodeDescription: "R scripts for the analyses",
  exampleCodeLicense: "MIT",
  results: "Your statements",
  dataHeading: "Data availability",
  codeHeading: "Code availability",
  copy: "Copy",
  copied: "Copied!",
  prompt: "Tick at least one case above to build the statement.",
  noticePlaceholders:
    "Replace every bracketed placeholder with the real detail before you submit.",
  noticeRequestPlos:
    "PLOS journals expect the data behind the findings to be public, and do not accept an author as the only contact for restricted data — name a data access or ethics committee instead.",
  noticeIdentifier:
    "Give each deposit its DOI or accession number. Publishers such as Springer Nature ask for persistent identifiers, not a link to the repository's home page.",
  noticeIncompleteDoi: "“{value}” looks like an incomplete DOI.",
  noticeCodeHost:
    "Links to GitHub, GitLab or Bitbucket can change. Archive a release in a repository that issues DOIs, such as Zenodo, and give that DOI instead.",
  noticeNature:
    "Nature Portfolio journals ask for a separate Code availability statement whenever custom code is central to the conclusions.",
  scopeAll: "All data",
  scopeOther: "Other data",
  articleOnly:
    "{scope} supporting the findings of this study are available within the article.",
  articleSupplement:
    "{scope} supporting the findings of this study are available within the article and its supplementary materials.",
  articleSupplementFiles:
    "{scope} supporting the findings of this study are available within the article and its supplementary materials ({files}).",
  described: " ({description})",
  whereLink: "in {repository} at {link}",
  whereAccession: "in {repository} under accession number {accession}",
  repositoryOpen:
    "The data that support the findings of this study{described} are openly available {where}.",
  repositoryEmbargo:
    "The data that support the findings of this study{described} will be openly available {where} when an embargo ends on {date}.",
  repositoryLicense: "They are shared under the {license} license.",
  publicData:
    "This study analyzed existing, publicly available data from {sources}.",
  requestReason: "The data are not publicly available because {reason}.",
  request:
    "The data that support the findings of this study are available from {contact} upon reasonable request.",
  correspondingAuthor: "the corresponding author",
  restrictedSubjectAll: "the data",
  restrictedSubject: "the {description}",
  thirdPartySubjectAll: "data",
  restrictedPrivacy:
    "Access to {subject} is restricted to protect the privacy of the research participants.",
  restrictedEthics:
    "Access to {subject} is restricted under the terms of the study's ethics approval.",
  restrictedLegal:
    "Access to {subject} is restricted for legal or commercial reasons.",
  restrictedThirdParty:
    "This study used {subject} obtained from {party} under a license that does not permit public sharing.",
  restrictedAccess: "Requests for access can be made to {contact}{details}.",
  contactDetails: " ({details})",
  restrictedConditions: "Access is subject to {conditions}.",
  noneNoData:
    "Data sharing is not applicable to this article, as no datasets were generated or analyzed during the current study.",
  noneTheoretical:
    "This article reports theoretical work; no data were generated or analyzed.",
  codeOpen:
    "The code used in this study{described} is openly available {where}.",
  codeLicense: "It is released under the {license} license.",
  codeRequest:
    "The code used in this study{described} is available from {contact} upon reasonable request.",
  codeNone: "No custom code was developed for this study.",
  codeNoneSoftware:
    "No custom code was developed for this study; the analyses were run in {software}.",
  placeholderRepository: "[repository name]",
  placeholderIdentifier: "[DOI or URL]",
  placeholderSources: "[sources]",
  placeholderParty: "[data owner]",
  placeholderContact: "[contact]",
  placeholderDate: "[date]",
  statementLocale: "en",
} as const;

type Strings = typeof DATA_AVAILABILITY_STATEMENT_GENERATOR_STRINGS;

const SCENARIO_ORDER: readonly DataScenarioId[] = [
  "article",
  "repository",
  "public",
  "restricted",
  "request",
  "none",
];

const SCENARIO_LABEL: Record<DataScenarioId, keyof Strings> = {
  article: "scenarioArticle",
  repository: "scenarioRepository",
  public: "scenarioPublic",
  restricted: "scenarioRestricted",
  request: "scenarioRequest",
  none: "scenarioNone",
};

const INITIAL_INPUTS: DataInputs = {
  scenarios: [],
  supplementary: false,
  files: "",
  repositories: [],
  publicSources: "",
  requestContact: "",
  requestReason: "",
  restriction: "privacy",
  restrictedDescription: "",
  party: "",
  accessContact: "",
  accessDetails: "",
  conditions: "",
  noDataReason: "noData",
};

const INITIAL_CODE: CodeInputs = {
  mode: "off",
  repository: "",
  identifier: "",
  description: "",
  license: "",
  contact: "",
  software: "",
};

function emptyRepository(id: number): RepositoryRow {
  return {
    id,
    repository: "",
    identifier: "",
    description: "",
    license: "",
    embargo: "",
  };
}

export default function DataAvailabilityStatementGeneratorWidget() {
  const strings = useToolUi(DATA_AVAILABILITY_STATEMENT_GENERATOR_STRINGS);
  const [inputs, setInputs] = useState<DataInputs>(INITIAL_INPUTS);
  const [repositories, setRepositories] = useState<RepositoryRow[]>([
    emptyRepository(1),
  ]);
  const [nextId, setNextId] = useState(2);
  const [code, setCode] = useState<CodeInputs>(INITIAL_CODE);

  const patch = (change: Partial<DataInputs>) =>
    setInputs((current) => ({ ...current, ...change }));
  const fullInputs: DataInputs = { ...inputs, repositories };
  const dataText = dataAvailabilityStatement(
    fullInputs,
    strings,
    strings.statementLocale
  );
  const codeText = codeAvailabilityStatement(code, strings);
  const notes = dataNotices(
    fullInputs,
    repositories,
    code,
    `${dataText} ${codeText}`,
    strings
  );
  const ticked = (id: DataScenarioId) => inputs.scenarios.includes(id);

  function loadExample() {
    setInputs({
      ...INITIAL_INPUTS,
      scenarios: ["repository", "restricted"],
      restrictedDescription: strings.exampleRestricted,
      accessContact: strings.exampleAccessContact,
      accessDetails: strings.exampleAccessDetails,
      conditions: strings.exampleConditions,
    });
    setRepositories([
      {
        ...emptyRepository(nextId),
        repository: strings.exampleRepository,
        identifier: strings.exampleIdentifier,
        description: strings.exampleDescription,
        license: strings.exampleLicense,
      },
    ]);
    setNextId((id) => id + 1);
    setCode({
      ...INITIAL_CODE,
      mode: "repository",
      repository: strings.exampleCodeRepository,
      identifier: strings.exampleCodeIdentifier,
      description: strings.exampleCodeDescription,
      license: strings.exampleCodeLicense,
    });
  }

  return (
    <div className="space-y-6">
      <fieldset className="space-y-3">
        <legend className={LABEL}>{strings.scenariosLegend}</legend>
        {SCENARIO_ORDER.map((id) => (
          <Checkbox
            checked={ticked(id)}
            key={id}
            label={strings[SCENARIO_LABEL[id]]}
            onChange={(on) =>
              patch({ scenarios: toggleScenario(inputs.scenarios, id, on) })
            }
          />
        ))}
      </fieldset>

      {ticked("article") ? (
        <ArticlePanel inputs={inputs} onChange={patch} strings={strings} />
      ) : null}
      {ticked("repository") ? (
        <RepositoryPanel
          onAdd={() => {
            setRepositories((rows) => [...rows, emptyRepository(nextId)]);
            setNextId((id) => id + 1);
          }}
          onRemove={(id) =>
            setRepositories((rows) => rows.filter((row) => row.id !== id))
          }
          onRowChange={(id, change) =>
            setRepositories((rows) =>
              rows.map((row) => (row.id === id ? { ...row, ...change } : row))
            )
          }
          rows={repositories}
          strings={strings}
        />
      ) : null}
      {ticked("public") ? (
        <PublicPanel inputs={inputs} onChange={patch} strings={strings} />
      ) : null}
      {ticked("restricted") ? (
        <RestrictedPanel inputs={inputs} onChange={patch} strings={strings} />
      ) : null}
      {ticked("request") ? (
        <RequestPanel inputs={inputs} onChange={patch} strings={strings} />
      ) : null}
      {ticked("none") ? (
        <NonePanel inputs={inputs} onChange={patch} strings={strings} />
      ) : null}

      <div className="space-y-4">
        <SelectField<CodeMode>
          id="das-code-mode"
          label={strings.codeLabel}
          onChange={(mode) => setCode((current) => ({ ...current, mode }))}
          options={[
            { value: "off", label: strings.codeOff },
            { value: "repository", label: strings.codeRepositoryOption },
            { value: "request", label: strings.codeRequestOption },
            { value: "none", label: strings.codeNoneOption },
          ]}
          value={code.mode}
        />
        <CodeFields
          code={code}
          onChange={(change) =>
            setCode((current) => ({ ...current, ...change }))
          }
          strings={strings}
        />
      </div>
      <ExampleButton label={strings.tryExample} onClick={loadExample} />

      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {dataText === "" ? (
          <Notice>{strings.prompt}</Notice>
        ) : (
          <StatementCard
            copiedLabel={strings.copied}
            copyLabel={strings.copy}
            heading={strings.dataHeading}
            paragraphs={[plainParagraph(dataText)]}
          />
        )}
        {codeText === "" ? null : (
          <StatementCard
            copiedLabel={strings.copied}
            copyLabel={strings.copy}
            heading={strings.codeHeading}
            paragraphs={[plainParagraph(codeText)]}
          />
        )}
        <NoticeList items={notes} />
      </section>
    </div>
  );
}
